// Stage Mail-1:Mail account 一覧行コンポーネント。
//
// 仕様(design §3.7):
// - 最大 3 行表示(design §3.7 「前端最大展示 3 行、超出滚动条 + 「さらに表示」按钮
//   は Mail-1 範囲外」)
// - 各行は:アカウント名 / email_address / status badge / 編集 / 連携解除
// - 連携解除は disconnect モーダルで確認

import React from "react";
import { Edit2, Unplug } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import type { MailAccount } from "../../api/mail";

export interface MailAccountListProps {
  accounts: MailAccount[];
  /** 編集ボタンが押された account.id */
  onEdit: (id: string) => void;
  /** 連携解除ボタンが押された account.id */
  onDisconnect: (id: string) => void;
  /** busy 状態 */
  busy?: "load" | "disconnect" | null;
  /** 行が描画上限を超えた数(将来用、現 Mail-1 ではモック数)。 */
  overflow?: number;
}

export function MailAccountList({
  accounts,
  onEdit,
  onDisconnect,
  busy = null,
  overflow = 0,
}: MailAccountListProps) {
  if (accounts.length === 0) {
    return (
      <div
        style={{
          padding: "16px",
          textAlign: "center",
          color: "#6b7280",
          fontSize: 13,
          background: "#f9fafb",
          border: "1px dashed #e5e7eb",
          borderRadius: 8,
        }}
      >
        登録済みの Mail アカウントはありません。「メールアカウントを追加」から連携できます。
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {accounts.map((acc) => (
        <div
          key={acc.id}
          style={{
            padding: "10px 14px",
            background: "#ffffff",
            border: `1px solid ${acc.is_active ? "#e5e7eb" : "#fecaca"}`,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                {acc.name}
              </span>
              {acc.is_active ? (
                <Badge color="green">{acc.has_imap_password && acc.has_smtp_password ? "接続済み" : "パスワード要設定"}</Badge>
              ) : (
                <Badge color="red">連携解除済み</Badge>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{acc.email_address}</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              IMAP: {acc.imap_host}:{acc.imap_port} ({acc.imap_security}) {" / "}
              SMTP: {acc.smtp_host}:{acc.smtp_port} ({acc.smtp_security})
            </div>
          </div>
          <div className="row gap-6" style={{ flexShrink: 0 }}>
            <Button
              variant="secondary"
              iconLeft={<Edit2 size={13} />}
              onClick={() => onEdit(acc.id)}
              disabled={busy !== null}
            >
              編集
            </Button>
            {acc.is_active && (
              <Button
                variant="ghost"
                iconLeft={<Unplug size={13} />}
                onClick={() => onDisconnect(acc.id)}
                disabled={busy !== null}
              >
                連携解除
              </Button>
            )}
          </div>
        </div>
      ))}
      {overflow > 0 && (
        <div style={{ textAlign: "center", fontSize: 12, color: "#6b7280" }}>
          他 {overflow} 件(スクロール / さらに表示は Mail-1 ではモック)
        </div>
      )}
    </div>
  );
}
